import { differenceWith, findIndex, includes } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { findDOMNode } from 'react-dom';
import {
    Button,
    ButtonDropdown,
    ButtonGroup,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    Collapse,
} from 'reactstrap';
import { bind } from '../Utils/Bind';
import { deepStringFind } from '../Utils/DeepStringFind';
import { mobxEqual } from '../Utils/MobxTools';
import { Optional } from '../Utils/Types';
import { BsExclamationTriangle } from '@react-icons/all-files/bs/BsExclamationTriangle';
import { BsXCircle } from '@react-icons/all-files/bs/BsXCircle';
import {BsCaretDownFill} from "@react-icons/all-files/bs/BsCaretDownFill";

export interface IGroupedOption<T> {
    group: string;
    options: T[];
}

export interface IInputDropdownMultiSelectFilterProps<T> extends React.Props<InputDropdownMultiSelectFilter<T>> {
    onToggle?(isOpen: boolean): void;
    onChange(filter: readonly T[] | null | undefined): void;
    print?(entity: T): string;
    sort?(a: T, b: T): number;
    comparer?(a: T, b: T): boolean;
    readonly all?: readonly T[];
    readonly groupedOptions?: readonly IGroupedOption<T>[];
    readonly options?: readonly T[];
    readonly filter?: readonly T[] | null;
    readonly title: string;
    readonly renderBefore?: React.ReactNode;
    readonly style?: React.CSSProperties;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly showActive: boolean;
    readonly showClear: boolean;
    readonly openOnMount?: boolean;
    readonly extraSelecteditems?: string;
    readonly useGroupOptions?: boolean;
}
@observer
export class InputDropdownMultiSelectFilter<T> extends React.Component<IInputDropdownMultiSelectFilterProps<T>> {
    public static defaultProps: Optional<IInputDropdownMultiSelectFilterProps<any>> = {
        showActive: true,
        showClear: true,
    };

    public constructor(props: IInputDropdownMultiSelectFilterProps<T>) {
        super(props);

        this.comparer = props.comparer ?? mobxEqual;
    }

    @action
    public componentDidMount() {
        if (this.props.openOnMount && !this.hasItems) {
            this.dropdownOpen = true;
            if (this.props.onToggle) this.props.onToggle(this.dropdownOpen);
        }
    }

    public render() {
        return (
            <div className="input-multi-select-dropdown-container">
                <ButtonGroup size="sm" className="ms-1 input-dropdown-multi-select-group" onKeyDown={this.onKeyDown}>
                    <ButtonDropdown
                        isOpen={this.dropdownOpen}
                        toggle={this.onToggle}
                        size="sm"
                        disabled={this.props.disabled}
                    >
                        <DropdownToggle color="primary" className="lighten">
                            {this.props.title}
                        </DropdownToggle>
                        {this.hasItems && (
                            <DropdownToggle color="primary" className="truncate">
                                {this.selectedItemsTitle}
                            </DropdownToggle>
                        )}
                        {this.props.extraSelecteditems && (
                            <DropdownToggle color="primary" className="truncate">
                                {this.props.extraSelecteditems}
                            </DropdownToggle>
                        )}{' '}
                        {this.hasConflicts && (
                            <DropdownToggle color="primary">
                                <BsExclamationTriangle className="text-danger" />
                            </DropdownToggle>
                        )}
                        <DropdownMenu
                            end
                            className={`${this.props.title}-input-dropdown-menu p-0 added-dropdown-width dropdown-height`}
                            ref={this.bindDropdownMenuRef}
                            onKeyDown={this.onMenuKeydown}
                        >
                            <Input
                                type="text"
                                placeholder={`Find ${this.props.title}...`}
                                value={this.findString}
                                onChange={this.onFindChange}
                                autoFocus
                            />
                            {this.props.renderBefore}

                            {this.hasItems && this.props.showClear && (
                                <button
                                    type="button"
                                    className="text-center dropdown-item border-bottom"
                                    onClick={this.clear}
                                >
                                    Clear Selected Items
                                </button>
                            )}

                            {!this.props.useGroupOptions &&
                                <div className="overflow-y overflow-x-hidden mh-500">
                                    {this.options?.filter(o => o != null).map(this.renderEntry)}
                                    
                                    {this.allOptions && this.allOptions.length > 0 && (
                                        <DropdownItem header>
                                            All {this.props.title}{' '}
                                        </DropdownItem>
                                    )}

                                    {this.allOptions?.map(this.renderEntry)}
                                    
                                    {this.props.children}
                                </div>
                            }

                            {this.props.useGroupOptions &&
                                <div className="overflow-y overflow-x-hidden mh-500">
                                    {this.allGroupedOptions.map(o => 
                                        <GroupEntry 
                                            onCheck={this.check} 
                                            onSelectAll={() => this.onSelectGroup(o)}
                                            groupedOption={o} 
                                            filter={this.props.filter} 
                                            comparer={this.comparer} 
                                            isSearching={!!this.findString}/>
                                    )}
                                    {this.props.children}
                                </div>
                            }

                        </DropdownMenu>
                    </ButtonDropdown>
                    <Button color="primary" onClick={this.clear} disabled={this.props.disabled}>
                        <BsXCircle/>
                    </Button>
                </ButtonGroup>
            </div>
        );
    }

    @bind
    private renderEntry(entry: T, key: any) {
        return (
            <div key={key} className="mt-1 mx-1 form-check-label ellipsis">
                <Input type="checkbox" checked={this.isChecked(entry)} onChange={event => this.check(event, entry)}/>

                {this.props.print ? this.props.print(entry) : entry}
            </div>
        );
    }

    private getFocusedChild(elements: HTMLCollection): HTMLButtonElement | undefined {
        const active = document.activeElement?.classList.contains('form-check-input')
            ? document.activeElement.parentElement?.parentElement
            : document.activeElement;
        if (active && includes(elements, active)) return active as HTMLButtonElement;
    }

    private getNextSibling(element: HTMLElement | null): HTMLDivElement | undefined {
        if (!element) return;
        if (element.classList?.contains('px-1')) return element as HTMLDivElement;

        return this.getNextSibling(element.nextSibling as HTMLElement);
    }

    private getPreviousSibling(element: HTMLElement | null): HTMLDivElement | undefined {
        if (!element) return;
        if (element.classList?.contains('px-1')) return element as HTMLDivElement;

        return this.getPreviousSibling(element.previousSibling as HTMLElement);
    }

    @bind
    @action
    private onMenuKeydown(event: React.KeyboardEvent) {
        const dropdownMenu = findDOMNode(this.dropdownMenuRef) as Element;
        const focusedChild = this.getFocusedChild(dropdownMenu.children);
        focusedChild?.classList.remove('active');

        if (event.keyCode == 40 && this.dropdownOpen) {
            const next = (
                focusedChild
                    ? this.getNextSibling(focusedChild.nextSibling as HTMLElement)
                    : dropdownMenu.firstElementChild
            ) as HTMLDivElement | null;
            if (next) {
                next.classList.add('active');
                next.focus();
                (next.querySelector('.form-check-input') as HTMLInputElement)?.focus();
            }
        }

        if (event.keyCode == 38 && this.dropdownOpen) {
            const previous = (
                focusedChild
                    ? this.getPreviousSibling(focusedChild.previousSibling as HTMLElement)
                    : dropdownMenu.firstElementChild
            ) as HTMLDivElement | null;
            if (previous) {
                previous.classList.add('active');
                previous.focus();
                (previous.querySelector('.form-check-input') as HTMLInputElement)?.focus();
            }
        }
    }

    @computed
    private get allOptions() {
        if (!this.props.all) return;

        let items = this.props.all.slice();

        if (this.findString)
            items = items.filter(e => deepStringFind(this.props.print ? this.props.print(e) : e, this.findString));

        if (this.props.options) items = differenceWith(items, this.props.options, this.comparer);

        if (this.props.sort) items = items.sort(this.props.sort);

        return items;
    }

    @computed
    private get options() {
        if (!this.props.options) return;

        let items = this.props.options.slice();

        if (this.findString)
            items = items.filter(e => deepStringFind(this.props.print ? this.props.print(e) : e, this.findString));

        if (this.props.sort) items = items.sort(this.props.sort);

        return items;
    }

    @computed
    private get allGroupedOptions(): IGroupedOption<T>[] {
        if (!this.props.groupedOptions) return [];
        
        let groupedItems: IGroupedOption<T>[] = [];

        // Iterate over each group in allGroupedOptions
        for (const groupedOption of this.props.groupedOptions) {
            let allItems = groupedOption.options;
            let group = groupedOption.group;
            
            if (this.findString) {
                allItems = allItems.filter(e => deepStringFind(this.props.print ? this.props.print(e) : e, this.findString));
            }
            
            if (this.props.sort) {
                allItems = allItems.sort(this.props.sort);
            }

            groupedItems.push({ group, options: allItems });
        }

        return groupedItems;
    }

    @computed
    private get selectedItemsTitle() {
        if (this.hasItems) {
            const strings = this.props.filter!.map(e => (this.props.print ? this.props.print(e) : e));
            return strings.join(', ');
        }
    }

    @computed
    private get hasItems() {
        return !!this.props.filter?.length;
    }

    @bind
    private isChecked(entity: T) {
        return findIndex(this.props.filter, e => this.comparer(e, entity)) != -1;
    }

    @computed
    private get hasConflicts() {
        return differenceWith(this.props.filter, this.props.options ?? [], this.comparer).length > 0;
    }

    @bind
    @action
    private onKeyDown(event: React.KeyboardEvent) {
        if (event.keyCode == 8 && this.findString.length > 0) return;

        if ((event.keyCode == 8 || event.keyCode === 13 || event.keyCode == 27) && this.dropdownOpen) {
            this.onToggle();
        }
    }

    @bind
    @action
    private onToggle() {
        this.dropdownOpen = !this.dropdownOpen;
        this.findString = '';
        if (this.props.onToggle) this.props.onToggle(this.dropdownOpen);
    }

    @bind
    @action
    private onFindChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.findString = target.value;
    }

    @bind
    @action
    private clear() {
        this.props.onChange(undefined);
    }

    @bind
    @action
    private check(event: React.FormEvent<HTMLInputElement>, entity: T) {
        const filter = this.props.filter ?? [];
        const target = event.target as HTMLInputElement;
        const checked = this.isChecked(entity);

        if (target.checked && !checked) {
            this.props.onChange(filter.concat(entity));
        } else if (!target.checked && checked) {
            this.props.onChange(filter.filter(e => !this.comparer(e, entity)));
        }
    }

    @bind
    @action
    private onSelectGroup(groupedOption: IGroupedOption<T>) {
        const filter = this.props.filter ?? [];
        const checked = groupedOption.options.every(element => filter.includes(element));

        if (!checked) {
            const missingElements = groupedOption.options.filter(element => !filter.includes(element));
            this.props.onChange(filter.concat(missingElements));
        } else if (checked) {
            this.props.onChange(filter.filter(element => !groupedOption.options.includes(element)));
        }
    }

    @bind
    private bindDropdownMenuRef(ref: DropdownMenu | null) {
        this.dropdownMenuRef = ref;
    }

    private comparer: (a: T, b: T) => boolean;
    @observable
    private findString: string = '';
    @observable
    private dropdownOpen = false;
    private dropdownMenuRef: DropdownMenu | null = null;
}

export interface IGroupEntry<T>  {
    print?(entity: T): string;
    comparer?(a: T, b: T): boolean;
    onSelectAll(): void;
    onCheck(event: React.FormEvent<HTMLInputElement>, entity: T): void;
    readonly groupedOption: IGroupedOption<T>;
    readonly isSearching: boolean;
    readonly filter?: readonly T[] | null;
}
@observer
export class GroupEntry<T> extends React.Component<IGroupEntry<T>> {
    public constructor(props: IGroupEntry<T>) {
        super(props);

        this.comparer = props.comparer ?? mobxEqual;
    }
    
    public render() {
        const {groupedOption} = this.props;
        return (
            <>
                {groupedOption.options.length > 0 &&
                    <>
                        <div className="px-2 nav-item d-flex align-items-center border-bottom">
                            {groupedOption.group}

                            <button className="btn btn-link" onClick={this.toggleCollapse}>
                                <BsCaretDownFill/>
                            </button>

                            <div className="ms-auto">
                                {this.selectedCount > 0 &&
                                    <span className="me-2">({this.selectedCount})</span>
                                }
                                <Input
                                    type="checkbox"
                                    checked={this.selectAllChecked}
                                    onChange={() => this.props.onSelectAll()}
                                />
                            </div>
                        </div>

                        <Collapse className="border-bottom" key={groupedOption.group} isOpen={this.props.isSearching ? true : this.isOpen}>
                            {groupedOption.options.map(this.renderEntry)}
                        </Collapse>
                    </>
                }
            </>
        );
    }
    
    @bind
    @action
    private onSelectAll(event: React.ChangeEvent<HTMLInputElement>) {
        for(let option of this.props.groupedOption.options) {
            this.props.onCheck(event, option);
        }
    }

    @bind
    private renderEntry(entry: T, key: any) {
        return (
            <div key={key} className="mt-1 mx-1 form-check-label ellipsis">
                <Input type="checkbox" checked={this.isChecked(entry)}
                       onChange={event => this.props.onCheck(event, entry)}/>

                {this.props.print ? this.props.print(entry) : entry}
            </div>
        );
    }

    @bind
    private isChecked(entity: T) {
        return findIndex(this.props.filter, e => this.comparer(e, entity)) != -1;
    }

    @bind
    @action
    private toggleCollapse() {
        this.isOpen = !this.isOpen;
    }
    
    @computed
    private get selectedCount(){
        let count = 0;
        for(let option of this.props.groupedOption.options) {
            if(this.props.filter?.includes(option)) count++;
        }
        
        return count;
    }
    
    @computed
    private get selectAllChecked(){
        return this.props.groupedOption.options.length == this.selectedCount;
    }

    @observable
    private isOpen = false;
    private comparer: (a: T, b: T) => boolean;
}
